import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import toast from 'react-hot-toast';
import { requiredMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';
import ConfirmModal from '../components/common/ConfirmModal';
import usePermissions from '../hooks/usePermissions';

const CARD = {
  background: 'var(--card-bg)', borderRadius: '10px', border: '1px solid var(--border)',
  padding: '18px', marginBottom: '14px',
};
const LABEL = { display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-primary)' };
const INPUT = { width: '100%', padding: '9px 11px', borderRadius: '7px', border: '1px solid var(--border)', fontSize: '0.86rem', background: '#fff', color: 'var(--text-primary)', boxSizing: 'border-box' };
const BTN_DARK = { background: '#1e293b', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer' };
const BTN_GHOST = { background: 'var(--page-bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' };
const BTN_GHOST_ACTIVE = { background: '#1e293b', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' };
const BADGE = { fontWeight: 700, borderRadius: 5, flexShrink: 0, background: '#f1f5f9', color: '#334155' };

function detectMediaType(file) {
  return file.type.startsWith('video/') ? 'video' : 'image';
}

export default function Gallery() {
  const { user } = useAuth();
  const { activeChurchId, canSwitchChurch } = useChurch();
  const churchId = canSwitchChurch ? activeChurchId : user?.church_id;
  const { isAdmin: isRealAdmin, hasGroupAccess, inGroupOnlyMode } = usePermissions();
  const canEditGallery = isRealAdmin ? !inGroupOnlyMode || hasGroupAccess('activities') : hasGroupAccess('activities');

  const [activities, setActivities] = useState([]);
  const [activityId, setActivityId] = useState('');
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [liveCaption, setLiveCaption] = useState('');
  const [livePosterFile, setLivePosterFile] = useState(null);
  const [addingLive, setAddingLive] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLinkUrl, setEditLinkUrl] = useState('');
  const [editLinks, setEditLinks] = useState([]);
  const [editPosterFile, setEditPosterFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchActivities = useCallback(async () => {
    if (!churchId) { setActivities([]); return; }
    try {
      const res = await api.get('/activities/', { params: { church_id: churchId, status: 'completed', page_size: 100 } });
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];
      setActivities(list);
    } catch {
      setActivities([]);
    }
  }, [churchId]);

  const fetchMedia = useCallback(async () => {
    if (!churchId) { setMedia([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = { church_id: churchId, page_size: 200 };
      if (activityId) params.activity = activityId;
      const res = await api.get('/activities/media/', { params });
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];
      setMedia(list);
    } catch {
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, [churchId, activityId]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);
  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!activityId) {
      toast.error("Choisissez d'abord une activité.");
      e.target.value = '';
      return;
    }
    setUploading(true);
    let ok = 0, failed = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('activity', activityId);
        fd.append('media_type', detectMediaType(file));
        fd.append('file', file);
        if (caption) fd.append('caption', caption);
        if (description) fd.append('description', description);
        if (linkUrl) fd.append('external_url', linkUrl);
        if (canSwitchChurch) fd.append('church_id', churchId);
        await api.post('/activities/media/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch {
        failed++;
      }
    }
    setUploading(false);
    e.target.value = '';
    setCaption('');
    setDescription('');
    setLinkUrl('');
    if (ok) toast.success(`${ok} média(s) ajouté(s).`);
    if (failed) toast.error(`${failed} échec(s).`);
    fetchMedia();
  };

  const handleDelete = async (item) => {
    try {
      await api.delete(`/activities/media/${item.id}/`);
      setMedia(prev => prev.filter(m => m.id !== item.id));
      toast.success('Média supprimé.');
    } catch {
      toast.error('Erreur lors de la suppression.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const [liveUrlError, setLiveUrlError] = useState('');

  const handleAddLive = async (e) => {
    e.preventDefault();
    if (!liveUrl.trim()) { setLiveUrlError(requiredMessage('Lien du direct')); return; }
    setLiveUrlError('');
    setAddingLive(true);
    try {
      const fd = new FormData();
      fd.append('media_type', 'live');
      fd.append('external_url', liveUrl.trim());
      fd.append('caption', liveCaption || 'Diffusion en direct');
      if (livePosterFile) fd.append('file', livePosterFile);
      if (canSwitchChurch) fd.append('church_id', churchId);
      await api.post('/activities/media/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLiveUrl('');
      setLiveCaption('');
      setLivePosterFile(null);
      toast.success('Direct ajouté à la galerie.');
      fetchMedia();
    } catch {
      toast.error("Erreur lors de l'ajout du direct.");
    } finally {
      setAddingLive(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditCaption(item.caption || '');
    setEditDescription(item.description || '');
    setEditLinkUrl(item.external_url || '');
    setEditLinks(item.links?.length > 0 ? item.links : []);
    setEditPosterFile(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editItem) return;
    setSavingEdit(true);
    try {
      const fd = new FormData();
      fd.append('caption', editCaption);
      fd.append('description', editDescription);
      fd.append('external_url', editLinkUrl);
      fd.append('links', JSON.stringify(editLinks.filter(l => l.url?.trim())));
      if (editPosterFile) fd.append('file', editPosterFile);
      await api.patch(`/activities/media/${editItem.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Média mis à jour.');
      setEditItem(null);
      fetchMedia();
    } catch {
      toast.error('Erreur lors de la mise à jour.');
    } finally {
      setSavingEdit(false);
    }
  };

  const togglePoster = async (item) => {
    try {
      await api.patch(`/activities/media/${item.id}/`, { is_poster: !item.is_poster });
      setMedia(prev => prev.map(m => m.id === item.id ? { ...m, is_poster: !m.is_poster } : m));
    } catch {
      toast.error('Erreur lors de la mise à jour.');
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '900px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Galerie</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Ajoutez des affiches, photos et vidéos aux activités déjà passées — visibles sur la page d'accueil et le portail fidèle.
        </p>
      </div>

      {canSwitchChurch && !churchId && (
        <div style={CARD}>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: 0 }}>
            Sélectionnez une église dans la barre du haut pour gérer sa galerie.
          </p>
        </div>
      )}

      {churchId && !canEditGallery && (
        <div style={CARD}>
          <p style={{ fontSize: '0.86rem', color: '#991b1b', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Accès non accordé par l'administrateur. Contactez votre administrateur pour obtenir le droit de gérer la galerie.
          </p>
        </div>
      )}

      {churchId && canEditGallery && (
        <>
          <div style={CARD}>
            <h2 style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px' }}>Ajouter des photos / vidéos</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={LABEL}>Activité (terminée)<Req /></label>
                <select value={activityId} onChange={e => setActivityId(e.target.value)} style={INPUT}>
                  <option value="">— Choisir une activité —</option>
                  {activities.map(a => <option key={a.id} value={a.id}>{a.name} — {a.date}</option>)}
                </select>
                {activities.length === 0 && (
                  <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', margin: '5px 0 0' }}>
                    Aucune activité terminée pour l'instant.
                  </p>
                )}
              </div>
              <div>
                <label style={LABEL}>Titre court (optionnel)</label>
                <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Ex : Culte de rentrée 2026" style={INPUT} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={LABEL}>Description détaillée (optionnelle)</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Décrivez ce moment en quelques phrases..." rows={2}
                  style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div>
                <label style={LABEL}>Lien de redirection (optionnel)</label>
                <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." style={INPUT} />
              </div>
            </div>

            <div>
              <label style={LABEL}>Fichiers</label>
              <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} disabled={uploading || !activityId}
                style={{ ...INPUT, cursor: uploading ? 'wait' : 'pointer' }} />
              {!activityId && <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', margin: '5px 0 0' }}>Choisissez d'abord l'activité pour pouvoir ajouter des fichiers.</p>}
              {uploading && <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '5px 0 0' }}>Envoi en cours...</p>}
            </div>
          </div>

          <div style={CARD}>
            <h2 style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>Diffusion en direct</h2>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              Collez un lien YouTube, TikTok ou Facebook Live — il apparaîtra dans la galerie avec un bouton pour regarder directement.
            </p>
            <form onSubmit={handleAddLive} noValidate>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={LABEL}>Lien du direct<Req /></label>
                  <input value={liveUrl} onChange={e => setLiveUrl(e.target.value)} placeholder="https://www.tiktok.com/@... ou https://www.youtube.com/watch?v=..." style={{ ...INPUT, ...(liveUrlError ? { borderColor: ERROR_COLOR } : {}) }} />
                  <FieldError message={liveUrlError} />
                </div>
                <div>
                  <label style={LABEL}>Titre (optionnel)</label>
                  <input value={liveCaption} onChange={e => setLiveCaption(e.target.value)} placeholder="Ex : Culte du dimanche en direct" style={INPUT} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={LABEL}>Image miniature du direct (optionnel)</label>
                <input type="file" accept="image/*" onChange={e => setLivePosterFile(e.target.files?.[0] || null)} style={{ fontSize: '0.85rem' }} />
                {livePosterFile && (
                  <div style={{ width: 80, height: 45, borderRadius: 6, overflow: 'hidden', marginTop: 6 }}>
                    <img src={URL.createObjectURL(livePosterFile)} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
              </div>
              <button type="submit" disabled={addingLive}
                style={{ ...BTN_DARK, padding: '9px 16px', fontSize: '0.84rem', cursor: addingLive ? 'wait' : 'pointer' }}>
                {addingLive ? 'Ajout...' : '+ Ajouter le direct'}
              </button>
            </form>
          </div>

          <div style={CARD}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>Médias</h2>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
              {activityId ? 'Filtré sur l\'activité sélectionnée.' : 'Tous les médias de cette église.'}
            </p>
            {loading ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement...</p>
            ) : media.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucun média pour le moment.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {media.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--page-bg)' }}>
                    <div style={{ width: 46, height: 46, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: m.media_type === 'live' ? '#1e293b' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {m.media_type === 'live' && !m.file_url ? (
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
                      ) : m.media_type === 'video' ? (
                        <video src={m.file_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                      ) : (
                        <img src={m.file_url} alt={m.caption} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.caption || m.activity_name || 'Sans titre'}
                        </span>
                        {m.media_type === 'live' && (
                          <span style={{ ...BADGE, fontSize: '0.6rem', padding: '2px 6px', letterSpacing: '0.03em', background: '#1e293b', color: '#fff' }}>EN DIRECT</span>
                        )}
                        {m.is_poster && (
                          <span style={{ ...BADGE, fontSize: '0.6rem', padding: '2px 6px' }}>AFFICHE</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.73rem', color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.activity_name && m.activity_name !== m.caption ? m.activity_name : ''}
                        {m.external_url && <span> · lien configuré</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={() => openEdit(m)}
                        style={{ ...BTN_GHOST, padding: '6px 10px', fontSize: '0.7rem' }}>
                        Modifier
                      </button>
                      {m.media_type !== 'live' && (
                        <button onClick={() => togglePoster(m)}
                          style={{ ...(m.is_poster ? BTN_GHOST_ACTIVE : BTN_GHOST), padding: '6px 10px', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                          {m.is_poster ? 'Affiche ✓' : 'Def. affiche'}
                        </button>
                      )}
                      <button onClick={() => setConfirmDelete(m)}
                        style={{ ...BTN_GHOST, padding: '6px 10px', fontSize: '0.7rem' }}>
                        Suppr.
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {editItem && (
        <div onClick={() => setEditItem(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'var(--card-bg)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                Modifier {editItem.media_type === 'live' ? 'le direct' : 'le média'}
              </h2>
              <button onClick={() => setEditItem(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>✕</button>
            </div>

            <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: 10, overflow: 'hidden', background: editItem.media_type === 'live' ? '#1e293b' : '#e2e8f0', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {editPosterFile ? (
                <img src={URL.createObjectURL(editPosterFile)} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : editItem.media_type === 'video' ? (
                <video src={editItem.file_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} controls />
              ) : editItem.file_url ? (
                <img src={editItem.file_url} alt={editItem.caption} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
              )}
            </div>

            <form onSubmit={handleSaveEdit}>
              <div style={{ marginBottom: 14 }}>
                <label style={LABEL}>Titre court</label>
                <input value={editCaption} onChange={e => setEditCaption(e.target.value)} placeholder="Titre" style={INPUT} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={LABEL}>Description détaillée (affichée dans l'aperçu)</label>
                <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} placeholder="Décrivez ce moment en quelques phrases..." rows={3}
                  style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={LABEL}>Lien de redirection {editItem.media_type === 'live' ? '(direct)' : '(optionnel)'}</label>
                <input value={editLinkUrl} onChange={e => setEditLinkUrl(e.target.value)} placeholder="https://..." style={INPUT} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={LABEL}>Liens supplémentaires (optionnel — Facebook, Instagram...)</label>
                {editLinks.map((l, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, marginBottom: 8 }}>
                    <input value={l.label} onChange={e => setEditLinks(links => links.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                      placeholder="Facebook" style={INPUT} />
                    <input value={l.url} onChange={e => setEditLinks(links => links.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                      placeholder="https://..." style={INPUT} />
                    <button type="button" onClick={() => setEditLinks(links => links.filter((_, j) => j !== i))}
                      style={{ ...BTN_GHOST, padding: '0 12px', fontSize: '0.8rem' }}>✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => setEditLinks(links => [...links, { label: '', url: '' }])}
                  style={{ ...BTN_GHOST, padding: '7px 12px', fontSize: '0.78rem' }}>
                  + Ajouter un lien
                </button>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={LABEL}>{editItem.media_type === 'live' ? 'Affiche (image miniature du direct)' : 'Remplacer le fichier'}</label>
                <input type="file" accept="image/*,video/*" onChange={e => setEditPosterFile(e.target.files?.[0] || null)} style={{ display: 'block', fontSize: '0.85rem' }} />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEditItem(null)}
                  style={{ ...BTN_GHOST, padding: '9px 16px', fontSize: '0.85rem' }}>
                  Annuler
                </button>
                <button type="submit" disabled={savingEdit}
                  style={{ ...BTN_DARK, padding: '9px 20px', fontSize: '0.85rem', cursor: savingEdit ? 'wait' : 'pointer' }}>
                  {savingEdit ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title={confirmDelete?.media_type === 'live' ? 'Supprimer ce direct ?' : 'Supprimer ce média ?'}
        message={
          confirmDelete?.media_type === 'live'
            ? `Le direct « ${confirmDelete?.caption || 'sans titre'} » sera retiré de la galerie et n'apparaîtra plus sur la page d'accueil.`
            : `« ${confirmDelete?.caption || confirmDelete?.activity_name || 'Ce média'} » sera définitivement supprimé de la galerie.`
        }
        confirmLabel="Supprimer"
        onConfirm={() => handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
